import React from 'react';
import Cookies from 'js-cookie';
import { FiAward, FiBookOpen, FiBriefcase, FiLayers, FiSearch, FiTarget, FiTool } from 'react-icons/fi';
import styles from './Competency.module.css';
import * as utils from '../../utils/utils';
import Sidebar from '../Sidebar/Sidebar';

const CATEGORY_CONFIG = {
	skill: {
		label: 'Skills',
		endpoint: '/competency/getSkills',
		responseKey: 'skills',
		icon: FiTarget,
	},
	tool: {
		label: 'Tools',
		endpoint: '/competency/getTools',
		responseKey: 'tools',
		icon: FiTool,
	},
	softSkill: {
		label: 'Soft Skills',
		endpoint: '/competency/getSoftSkills',
		responseKey: 'softSkills',
		icon: FiLayers,
	},
	domain: {
		label: 'Domains',
		endpoint: '/competency/getDomains',
		responseKey: 'domains',
		icon: FiBriefcase,
	},
	certificate: {
		label: 'Certificates',
		endpoint: '/competency/getCertificates',
		responseKey: 'certificates',
		icon: FiAward,
	},
};

const CATEGORY_KEYS = Object.keys(CATEGORY_CONFIG);

export default class Competency extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			activeCategory: 'skill',
			itemsByCategory: {},
			isLoading: true,
			errorText: '',
			searchText: '',
			selectedEntityName: 'all',
		};
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}

		this.loadCategory('skill', true);
	}

	getUserIDKey = () => {
		if (utils.isDev) {
			return 'User-Id-Dev';
		}

		return 'User-Id';
	};

	getCurrentUserID = () => {
		const userID = Cookies.get(this.getUserIDKey());
		return Number(userID);
	};

	getStatusMeta = (progressStatusId) => {
		switch (Number(progressStatusId)) {
			case 1:
				return { label: 'Foundation', tone: 'foundation' };
			case 2:
				return { label: 'Developing', tone: 'developing' };
			case 3:
				return { label: 'Proficient', tone: 'proficient' };
			case 4:
				return { label: 'Advanced', tone: 'advanced' };
			case 5:
				return { label: 'Expert', tone: 'expert' };
			default:
				return { label: 'Tracked', tone: 'tracked' };
		}
	};

	normalizeItems = (items, categoryKey) => {
		return (items || []).map((item, index) => {
			const statusMeta = this.getStatusMeta(item.progressStatusId);

			return {
				id: `${categoryKey}-${item.name || 'item'}-${item.entityName || 'entity'}-${index}`,
				name: item.name || 'Untitled competency',
				description: item.description || '',
				entityName: item.entityName || 'General',
				progressStatusId: Number(item.progressStatusId) || 0,
				statusLabel: statusMeta.label,
				statusTone: statusMeta.tone,
				issueBy: item.issueBy || '',
				year: item.year || '',
			};
		});
	};

	loadCategory = (categoryKey, forceRefresh = false) => {
		const category = CATEGORY_CONFIG[categoryKey];
		const {
			itemsByCategory,
		} = this.state;

		if (!category) {
			return;
		}

		if (!forceRefresh && Array.isArray(itemsByCategory[categoryKey])) {
			this.setState({
				activeCategory: categoryKey,
				isLoading: false,
				errorText: '',
				selectedEntityName: 'all',
			});
			return;
		}

		const userID = this.getCurrentUserID();
		if (!Number.isInteger(userID) || userID <= 0) {
			this.setState({
				isLoading: false,
				errorText: 'Could not identify your account. Please sign in again.',
			});
			return;
		}

		this.setState({
			activeCategory: categoryKey,
			isLoading: true,
			errorText: '',
			selectedEntityName: 'all',
		}, () => {
			utils.sendRequest(category.endpoint, { userID })
				.then((responseData) => {
					const list = this.normalizeItems(responseData[category.responseKey] || [], categoryKey);

					this.setState((prevState) => ({
						isLoading: false,
						itemsByCategory: {
							...prevState.itemsByCategory,
							[categoryKey]: list,
						},
					}));
				})
				.catch((error) => {
					this.setState({
						isLoading: false,
						errorText: error && error.message ? error.message : 'Could not load competencies right now.',
					});
				});
		});
	};

	onCategoryClick = (categoryKey) => {
		this.loadCategory(categoryKey);
	};

	onSearchChange = (event) => {
		this.setState({
			searchText: event.target.value,
		});
	};

	onEntityFilterChange = (event) => {
		this.setState({
			selectedEntityName: event.target.value,
		});
	};

	getActiveItems = () => {
		const {
			activeCategory,
			itemsByCategory,
		} = this.state;

		return itemsByCategory[activeCategory] || [];
	};

	getEntityOptions = (items) => {
		const entities = new Set();
		items.forEach((item) => {
			if (item.entityName) {
				entities.add(item.entityName);
			}
		});

		return Array.from(entities).sort((a, b) => a.localeCompare(b));
	};

	getFilteredItems = (items) => {
		const {
			searchText,
			selectedEntityName,
		} = this.state;

		const normalizedSearchText = searchText.trim().toLowerCase();

		return items.filter((item) => {
			if (selectedEntityName !== 'all' && item.entityName !== selectedEntityName) {
				return false;
			}

			if (normalizedSearchText === '') {
				return true;
			}

			const searchableText = `${item.name} ${item.description} ${item.entityName} ${item.issueBy}`.toLowerCase();
			return searchableText.includes(normalizedSearchText);
		});
	};

	renderCategoryTabs = () => {
		const {
			activeCategory,
			isLoading,
		} = this.state;

		return (
			<div className={styles.tabRow}>
				{CATEGORY_KEYS.map((categoryKey) => {
					const category = CATEGORY_CONFIG[categoryKey];
					const Icon = category.icon;
					const isActive = activeCategory === categoryKey;

					return (
						<button
							key={categoryKey}
							type='button'
							className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ''}`}
							onClick={() => this.onCategoryClick(categoryKey)}
							disabled={isLoading && !isActive}
						>
							<Icon />
							<span>{category.label}</span>
						</button>
					);
				})}
			</div>
		);
	};

	renderStatCards = (items, filteredItems) => {
		const entityCount = this.getEntityOptions(items).length;

		return (
			<div className={styles.statsRow}>
				<div className={styles.statCard}>
					<span className={styles.statLabel}>Total</span>
					<strong className={styles.statValue}>{items.length}</strong>
				</div>

				<div className={styles.statCard}>
					<span className={styles.statLabel}>Entities</span>
					<strong className={styles.statValue}>{entityCount}</strong>
				</div>

				<div className={styles.statCard}>
					<span className={styles.statLabel}>Visible</span>
					<strong className={styles.statValue}>{filteredItems.length}</strong>
				</div>
			</div>
		);
	};

	renderFilters = (entityOptions) => {
		const {
			searchText,
			selectedEntityName,
		} = this.state;

		return (
			<div className={styles.filterRow}>
				<div className={styles.searchWrap}>
					<FiSearch className={styles.searchIcon} />
					<input
						type='text'
						value={searchText}
						onChange={this.onSearchChange}
						className={styles.searchInput}
						placeholder='Search by name, context, issuer, or description'
					/>
				</div>

				<div className={styles.selectWrap}>
					<select
						value={selectedEntityName}
						onChange={this.onEntityFilterChange}
						className={styles.selectInput}
					>
						<option value='all'>All entities</option>
						{entityOptions.map((entityName) => {
							return (
								<option key={entityName} value={entityName}>
									{entityName}
								</option>
							);
						})}
					</select>
				</div>
			</div>
		);
	};

	renderList = (items) => {
		const {
			activeCategory,
		} = this.state;

		if (items.length === 0) {
			return (
				<div className={styles.emptyState}>
					<FiBookOpen />
					<h3>No records found</h3>
					<p>Try adjusting filters, or add more verified updates in your workflow to populate this view.</p>
				</div>
			);
		}

		return (
			<div className={styles.cardGrid}>
				{items.map((item) => {
					const categoryLabel = CATEGORY_CONFIG[activeCategory].label;

					return (
						<article key={item.id} className={styles.competencyCard}>
							<div className={styles.cardTopRow}>
								<span className={styles.entityBadge}>{item.entityName}</span>
								<span className={`${styles.statusBadge} ${styles[`status-${item.statusTone}`]}`}>{item.statusLabel}</span>
							</div>

							<h3 className={styles.cardTitle}>{item.name}</h3>
							<p className={styles.cardDescription}>{item.description || 'No additional description provided yet.'}</p>

							<div className={styles.metaRow}>
								<span className={styles.metaItem}>{categoryLabel}</span>
								{item.issueBy && <span className={styles.metaItem}>Issued by {item.issueBy}</span>}
								{item.year && <span className={styles.metaItem}>Year {item.year}</span>}
							</div>
						</article>
					);
				})}
			</div>
		);
	};

	renderMainContent = () => {
		const {
			isLoading,
			errorText,
		} = this.state;
		const items = this.getActiveItems();
		const filteredItems = this.getFilteredItems(items);
		const entityOptions = this.getEntityOptions(items);

		return (
			<>
				{this.renderCategoryTabs()}

				{this.renderStatCards(items, filteredItems)}

				{this.renderFilters(entityOptions)}

				{isLoading && <div className={styles.infoBox}>Loading competencies...</div>}
				{!isLoading && this.renderList(filteredItems)}

				{errorText !== '' && <div className={styles.errorBox}>{errorText}</div>}
			</>
		);
	};

	render() {
		const {
			activeCategory,
		} = this.state;

		return (
			<div className={styles.pageContainer}>
				<Sidebar />

				<div className={styles.glowTop} />
				<div className={styles.glowBottom} />

				<div className={styles.layoutShell}>
					<section className={styles.headerPanel}>
						<div className={styles.brandBadge}>HQVibe</div>
						<h1 className={styles.title}>Competency Matrix</h1>
						<p className={styles.subtitle}>
							Review your verified competencies across skills, tools, soft skills, domains, and certificates.
						</p>
						<div className={styles.userRow}>
							<span className={styles.userCaption}>Current view: {CATEGORY_CONFIG[activeCategory].label}</span>
						</div>
					</section>

					<section className={styles.mainCardPanel}>
						<h2 className={styles.formTitle}>Professional Competency Ledger</h2>
						<p className={styles.formText}>Switch categories and refine results by search and entity context.</p>

						{this.renderMainContent()}
					</section>
				</div>
			</div>
		);
	}
}
